import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { environment as env } from '../../../environments/environment';
import { Package, TablePackage } from '../../models/package.model';
import { NotificationsService } from '../../services/notifications.service';
import { ShoppingCartService } from '../../services/shoppingcart.service';
import { trigger, transition, animate, state, style } from '@angular/animations';
import { CodesApiService } from '../../services/codes-api.service';
import { Product } from '../../models/product.model';
import { FilterService } from '../../services/filter.service';
import { MenuService } from '../../services/menu.service';

@Component({
  selector: 'app-package-list-table',
  templateUrl: './package-list-table.component.html',
  styleUrls: ['./package-list-table.component.scss'],
  providers: [DatePipe],
  animations: [
    trigger('blockInitialRenderAnimation', [transition(':enter', [])]),
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageListTableComponent implements OnInit, OnDestroy {

  private subscriptions = new Map<string, Subscription>();

  secondaryColumns: any[];
  private defaultLengthsTemplate: Record<string, string>;
  dataSource: MatTableDataSource<TablePackage> = new MatTableDataSource();
  displayedColumnsDefaults = ['id', 'warehouse', 'certificate', 'landed', 'shippingMark', 'volume', 'weight', 'pcs'];
  displayedColumns: string[];
  packetLengths = env.packetLengths || [180, 210, 240, 270, 300, 330, 360, 390, 420, 450, 480, 510, 540, 570, 600];
  selectedCache = {};
  isValid: true;

  minPrice = env.minPrice || 50;
  maxPrice = env.maxPrice || 2000;

  @Input() product$: Observable<Product>;
  @Input() locale: string;
  @Input() currency: string;
  @Input() readOnly: boolean;
  @Input() internalUser: boolean;
  @Input() cart: boolean;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    private notification: NotificationsService,
    private datePipe: DatePipe,
    public shoppingCartService: ShoppingCartService,
    private translate: TranslateService,
    public filterService: FilterService,
    public codesApiService: CodesApiService,
  ) { }

  ngOnInit() {
    this.locale = this.translate.currentLang;
    this.displayedColumns = [...this.displayedColumnsDefaults, ...this.packetLengths.map(i => `length-${i}`)];
    // setup a default string list for the package lengths
    this.defaultLengthsTemplate = Object.freeze(this.packetLengths.reduce((list, i) => {
      list[`length-${i}`] = null;
      return list;
    }, {}));

    // cached selected packages for performance
    const reducer = (list, pack) => { list[pack.id] = true; return list; };
    this.selectedCache = this.shoppingCartService.contents().reduce(reducer, {});
    this.subscriptions.set('shoppingCartItemsChanged',
      this.shoppingCartService.itemsChanged.subscribe(packages => this.selectedCache = packages.reduce(reducer, {}))
    );

    this.sort.sort(<MatSortable>{ id: 'sortKey', start: 'asc' });
    this.dataSource.sort = this.sort;
    this.subscriptions.set('product$', this.product$
      .subscribe(product => this.dataSource.data = this.getTableData(product))
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  getTableData(product: Product) {

    // const data = this.$packages.map(pack => {
    const data = product.packages.map(pack => {
      // setup fixed list of item lengths
      // summary is used for mobile view summary row
      const modulePieces = pack.lengthDistribution && pack.lengthDistribution.modulePieces;
      const summary = modulePieces.map(item => `${item.length}: ${item.value}`);
      const packageLengths = modulePieces && modulePieces.reduce((list, item) => {
        list[`length-${item.length}`] = item.value;
        return list;
      }, {}) || {};
      const lengths = Object.assign({}, this.defaultLengthsTemplate, packageLengths);
      // setup a Package token, that represents the table data of a Package
      const landed = pack.arrivalDate || pack.estimatedArrivalDate;
      const result = {
        id: pack.number,
        warehouseCode: pack.warehouseCode,
        certificate: pack.certificateName,
        landed,
        hasLanded: pack.statusCode !== 'VESS',
        shippingMark: pack.shippingMark,
        volume: pack.volume.value,
        weight: pack.netWeight.value,
        pcs: pack.pieces.value,
        sortKey: `${landed || '?'}-${pack.number}`,
        lengths: lengths,
        summary: summary.join('    '), // NOTE: the join is on 4 'NO-BREAK SPACE' (U+00A0) to prevent HTML from collapsing the whitespace
        // payload is the original Package
        payload: pack
      };
      return result;
    });

    const rows = [];
    data.forEach(pack => rows.push(pack));
    // data.forEach(pack => rows.push(pack, {
    //   isSummary: true, sortKey: pack.sortKey + 'x', hasLanded: pack.hasLanded, summary: pack.summary, payload: pack.payload
    // }));
    return rows;
  }

  isSelected(pkg: Package): boolean {
    // console.log('isSelected', pkg.id, this.shoppingCartService.hasId(pkg.id));
    // return pkg ? this.shoppingCartService.hasId(pkg.id) : false;
    return pkg ? this.selectedCache && this.selectedCache[pkg.id] : false;
  }

  onSelectedRow(pkg: TablePackage): void {
    if (this.readOnly || !pkg.hasLanded) {
      return;
    }

    if (!pkg.payload.arrivalDate) {
      this.notification.send(
        'NOTIFICATIONS.TEXT_PACKAGE_NOT_AVAILABLE', {
        date: this.datePipe.transform(pkg.payload.estimatedArrivalDate, 'shortDate', undefined, this.locale)
      });
      return;
    }

    if (!this.cart) {
      this.shoppingCartService.toggle(pkg.payload);
    }
  }

  onDeselect(pkg: TablePackage): void {
    this.shoppingCartService.remove(pkg.payload);
  }

  trackByPackageId(pkg: TablePackage) {
    return pkg.id;
  }

  validPrice(price: number) {
    return price >= this.minPrice && price <= this.maxPrice;
  }

}
